import { useCallback, useEffect, useState } from 'react';

import { ValueType } from 'components/DebounceSelect';
import { useServiceFeatures } from 'hooks/useServiceContext';
import { getCustomers } from 'lib/api/getCustomers';
import { getLTLCustomersSearch } from 'lib/api/getLTLCustomersSearch';
import { searchCustomers } from 'lib/api/searchCustomers';
import { IntegrationCore } from 'types/Integration';
import { TMSCustomer } from 'types/Load';
import { Maybe } from 'types/UtilityTypes';
import { IntegrationType, TMS } from 'types/enums/Integrations';
import {
  GenericCompanySearchableFields,
  customerSearchHandler,
  injectSelectedObject,
  mapCustomerToSelectOptions,
} from 'utils/loadInfoAndBuilding';

interface UseCustomersResult {
  customers: Maybe<TMSCustomer[]>;
  isLoading: boolean;
  tmsTenant: Maybe<string>;
  refreshCustomers: () => Promise<void>;
  resetCustomerSearch: () => void;
  ensureCustomerByExternalTmsId: (externalTMSID: string) => Promise<void>;
  customerSearch: (
    field: GenericCompanySearchableFields,
    value: string
  ) => Promise<ValueType[]>;
}

interface UseCustomersOptions {
  /** Toast function for displaying notifications. Optional - if not provided, no toasts will be shown */
  toast?: (params: {
    description: string;
    variant: 'success' | 'destructive';
  }) => void;
  /** Suggested customer ID to preserve when resetting search. Optional */
  suggestedCustomerId?: string;
  isLTL?: boolean;
}

// TODO: Move to BE StaticTMSInterface
const customerSupportedTMSes: TMS[] = [
  TMS.McleodEnterprise,
  TMS.Turvo,
  TMS.Aljex,
  TMS.ThreeG,
  TMS.Revenova,
  TMS.MercurygateBoomi,
];

const ltlCustomerSupportedTMSes: TMS[] = [TMS.Tai, TMS.ThreeG, TMS.Turvo];

export const isCustomerSupportedTMS = (
  tmsIntegrations: Maybe<IntegrationCore[]>
) =>
  tmsIntegrations?.some((tms) =>
    customerSupportedTMSes.includes(tms.name as TMS)
  );

export const isLtlCustomerSupportedTMS = (
  tmsIntegrations: Maybe<IntegrationCore[]>
) =>
  tmsIntegrations?.some((tms) =>
    ltlCustomerSupportedTMSes.includes(tms.name as TMS)
  );

type ActiveLtlIntegrationOptions = {
  selectedIntegrationId?: Maybe<number>;
  selectedIntegrationName?: TMS | string;
};

export const getActiveLtlIntegration = (
  tmsIntegrations: Maybe<IntegrationCore[]>,
  options: ActiveLtlIntegrationOptions = {}
) => {
  if (!tmsIntegrations?.length) {
    return undefined;
  }

  if (options.selectedIntegrationId != null) {
    const byId = tmsIntegrations.find(
      (tms) => tms.id === options.selectedIntegrationId
    );

    if (byId) {
      return byId;
    }
  }

  if (options.selectedIntegrationName) {
    const byName = tmsIntegrations.find(
      (tms) => tms.name === options.selectedIntegrationName
    );

    if (byName) {
      return byName;
    }
  }

  return (
    tmsIntegrations.find((tms) => tms.name === TMS.ThreeG) ??
    tmsIntegrations.find((tms) => tms.name === TMS.Tai) ??
    tmsIntegrations[0]
  );
};

// Centralised hook that encapsulates all customer-list behaviour shared
// between QuickQuoteForm and BatchQuoteForm.

export function useCustomers(
  tmsIntegrations: Maybe<IntegrationCore[]>,
  options: UseCustomersOptions = {}
): UseCustomersResult {
  const { toast, suggestedCustomerId } = options;
  const isLTLMode = Boolean(options.isLTL);
  const { quotingIntegrations } = useServiceFeatures();
  const [customers, setCustomers] = useState<Maybe<TMSCustomer[]>>(null);
  const [initialCustomers, setInitialCustomers] =
    useState<Maybe<TMSCustomer[]>>(null);
  const [tmsTenant, setTMSTenant] = useState<Maybe<string>>(null);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [hasAttemptedInitialFetch, setHasAttemptedInitialFetch] =
    useState(false);

  let supportedTMSFunc = isCustomerSupportedTMS;
  if (options.isLTL) {
    supportedTMSFunc = isLtlCustomerSupportedTMS;
  }

  const isSupportedTMS = supportedTMSFunc(tmsIntegrations);

  const fetchCustomers = useCallback(
    async (forceRefresh = false) => {
      if (isLTLMode) {
        setIsLoading(false);
        return;
      }

      if (!tmsIntegrations?.length || !isSupportedTMS) {
        return;
      }

      setHasAttemptedInitialFetch(true);
      setIsLoading(true);

      const integrationId = tmsIntegrations?.[0]?.id;
      if (!integrationId) {
        setIsLoading(false);
        return;
      }

      const res = await getCustomers(integrationId, forceRefresh);
      if (res.isOk()) {
        // Limit initial customers to 100 to avoid performance issues
        const customers = res.value.customerList.slice(0, 100);

        setCustomers(customers);
        setInitialCustomers(customers);
        setTMSTenant(res.value.tmsTenant);

        // After initial fetch, if we have an AI suggestion not in the list, fetch and merge it
        if (
          suggestedCustomerId &&
          !customers.some((c) => c.externalTMSID === suggestedCustomerId)
        ) {
          const suggestedRes = await searchCustomers(
            integrationId,
            'externalTMSID' as GenericCompanySearchableFields,
            suggestedCustomerId
          );
          if (suggestedRes.isOk() && suggestedRes.value.customerList?.length) {
            const [suggestedCustomer] = suggestedRes.value.customerList;
            setCustomers((prev) => [...(prev ?? []), suggestedCustomer]);
            setInitialCustomers((prev) => [...(prev ?? []), suggestedCustomer]);
          }
        }
      }
      setIsLoading(false);
    },
    [tmsIntegrations, isSupportedTMS, isLTLMode, suggestedCustomerId]
  );

  // initial fetch
  useEffect(() => {
    if (isLTLMode) {
      setIsLoading(false);
      return;
    }
    fetchCustomers();
  }, [fetchCustomers, isLTLMode]);

  const fetchAndMergeSuggestedCustomer = async (
    integrationId: number,
    suggestedCustomerId: string
  ) => {
    const res = await searchCustomers(
      integrationId,
      'externalTMSID' as GenericCompanySearchableFields,
      suggestedCustomerId
    );

    if (!res.isOk() || !res.value.customerList?.length) {
      return;
    }

    const [suggestedCustomer] = res.value.customerList;
    setCustomers((prev) => [...(prev ?? []), suggestedCustomer]);
    setInitialCustomers((prev) => [...(prev ?? []), suggestedCustomer]);
  };

  // When we have an AI suggestion that it's not in initialCustomers,
  // fetch it and merge into the list in order to have the full customer model.
  useEffect(() => {
    if (!suggestedCustomerId || !tmsIntegrations?.length) {
      return;
    }

    // Check if the suggested customer is already in the initialCustomers list
    if (
      initialCustomers?.some((c) => c.externalTMSID === suggestedCustomerId)
    ) {
      return;
    }

    const integrationId = isLTLMode
      ? getActiveLtlIntegration(tmsIntegrations)?.id
      : tmsIntegrations[0]?.id;

    if (!integrationId) {
      return;
    }

    fetchAndMergeSuggestedCustomer(integrationId, suggestedCustomerId);
  }, [suggestedCustomerId, tmsIntegrations, initialCustomers, isLTLMode]);

  const refreshCustomers = useCallback(async () => {
    if (isLTLMode) {
      setIsLoading(false);
      return;
    }

    if (!hasAttemptedInitialFetch) {
      setIsLoading(false);
      return;
    }

    if (!tmsIntegrations?.length || !isSupportedTMS) {
      return;
    }

    setIsLoading(true);
    const integrationId = tmsIntegrations?.[0]?.id;
    if (!integrationId) {
      setIsLoading(false);
      return;
    }

    const res = await getCustomers(integrationId, true);
    if (res.isOk()) {
      setInitialCustomers(res.value.customerList);
      setCustomers(res.value.customerList);
      setTMSTenant(res.value.tmsTenant);

      if (toast) {
        toast({
          description: 'Successfully refreshed customer list.',
          variant: 'success',
        });
      }
    } else {
      if (toast) {
        toast({
          description: 'Error while refreshing customer list.',
          variant: 'destructive',
        });
      }
    }
    setIsLoading(false);
  }, [
    tmsIntegrations,
    isSupportedTMS,
    toast,
    isLTLMode,
    hasAttemptedInitialFetch,
  ]);

  const ensureCustomerByExternalTmsId = useCallback(
    async (externalTMSID: string) => {
      const trimmedExternalTMSID = externalTMSID.trim();
      if (
        !trimmedExternalTMSID ||
        !tmsIntegrations?.length ||
        !isSupportedTMS
      ) {
        return;
      }

      const existingCustomer = (customers ?? initialCustomers)?.find(
        (customer) => customer.externalTMSID === trimmedExternalTMSID
      );

      if (existingCustomer) {
        setCustomers((prev) =>
          injectSelectedObject(existingCustomer, prev ?? [])
        );
        setInitialCustomers((prev) =>
          injectSelectedObject(existingCustomer, prev ?? [])
        );
        return;
      }

      const integrationId = isLTLMode
        ? getActiveLtlIntegration(tmsIntegrations)?.id
        : tmsIntegrations[0]?.id;

      if (!integrationId) {
        return;
      }

      const res = await searchCustomers(
        integrationId,
        'externalTMSID',
        trimmedExternalTMSID
      );

      if (!res.isOk() || !res.value.customerList?.length) {
        return;
      }

      const [customer] = res.value.customerList;
      setCustomers((prev) => injectSelectedObject(customer, prev ?? []));
      setInitialCustomers((prev) => injectSelectedObject(customer, prev ?? []));
    },
    [customers, initialCustomers, isLTLMode, isSupportedTMS, tmsIntegrations]
  );

  const resetCustomerSearch = useCallback(() => {
    if (isLTLMode) {
      if (initialCustomers) {
        setCustomers(initialCustomers);
      }
      return;
    }

    // Always preserve any customers that were fetched via suggestions
    if (suggestedCustomerId && customers && initialCustomers) {
      const suggestedCustomer = customers.find(
        (c) => c.externalTMSID === suggestedCustomerId
      );
      if (suggestedCustomer) {
        // Always preserve the suggested customer, regardless of whether it's in initialCustomers
        const customersWithoutSuggested = initialCustomers.filter(
          (c) => c.externalTMSID !== suggestedCustomerId
        );
        setCustomers([...customersWithoutSuggested, suggestedCustomer]);
        return;
      }
    }

    setCustomers(initialCustomers);
  }, [suggestedCustomerId, customers, initialCustomers, isLTLMode]);

  const customerSearch = async (
    field: GenericCompanySearchableFields,
    value: string
  ): Promise<ValueType[]> => {
    if (isLTLMode) {
      const activeLtlIntegration = getActiveLtlIntegration(tmsIntegrations);
      const pricingIntegration = quotingIntegrations?.find(
        (integration) =>
          integration.type === IntegrationType.Quoting &&
          integration.name === activeLtlIntegration?.name
      );

      if (field === 'externalTMSID' && value.length >= 1) {
        const integrationId = activeLtlIntegration?.id ?? 0;
        if (integrationId) {
          return customerSearchHandler({
            tmsID: integrationId,
            customers,
            setCustomers,
            field: 'externalTMSID',
            value,
          });
        }
        return mapCustomerToSelectOptions(initialCustomers ?? customers ?? []);
      }

      if (value.length > 1) {
        const searchRes = await getLTLCustomersSearch(value, {
          tmsIntegrationId: activeLtlIntegration?.id,
          pricingIntegrationId: pricingIntegration?.id,
          integrationName: activeLtlIntegration?.name,
        });

        if (searchRes.isOk()) {
          const { customerList: searchedCustomers, tmsTenant: searchTenant } =
            searchRes.value;

          setCustomers(searchedCustomers);
          setInitialCustomers(searchedCustomers);

          if (searchTenant) {
            setTMSTenant(searchTenant);
          }

          return searchedCustomers && searchedCustomers.length
            ? mapCustomerToSelectOptions(searchedCustomers)
            : [];
        }

        if (toast) {
          toast({
            description: 'Failed to find LTL customers.',
            variant: 'destructive',
          });
        }

        return mapCustomerToSelectOptions(initialCustomers ?? customers);
      }

      const searchSource = initialCustomers ?? customers ?? [];
      const filteredResults = searchSource.filter((customer) => {
        const searchableText =
          `${customer.name} ${customer.addressLine1}`.toLowerCase();
        return searchableText.includes(value.toLowerCase());
      });

      setCustomers(filteredResults);
      return mapCustomerToSelectOptions(filteredResults);
    }

    const integrationId = tmsIntegrations?.[0]?.id ?? 0;
    return customerSearchHandler({
      tmsID: integrationId,
      customers,
      setCustomers,
      field,
      value,
    });
  };

  return {
    customers,
    isLoading,
    tmsTenant,
    refreshCustomers,
    resetCustomerSearch,
    ensureCustomerByExternalTmsId,
    customerSearch,
  };
}
